import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Cake, LogOut, Search, RefreshCw, Eye, Trash2, 
  Package, Clock, CheckCircle, Truck, XCircle, ChefHat,
  Phone, MapPin, Calendar, CreditCard, Banknote, ImageIcon, Edit, Save,
  DollarSign, CheckCircle2, Share2, ExternalLink, Copy, Bell, Send, MessageSquare,
  MessageCircle, PartyPopper, Plus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WhatsApp helper function - formats phone and opens WhatsApp with pre-filled message
const openWhatsApp = (phone, message) => {
  // Clean phone number - remove spaces, dashes, parentheses
  let cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Ensure it starts with country code
  if (cleanPhone.startsWith('876')) {
    cleanPhone = '1' + cleanPhone;
  } else if (!cleanPhone.startsWith('1') && !cleanPhone.startsWith('+')) {
    cleanPhone = '1876' + cleanPhone.slice(-7);
  }
  cleanPhone = cleanPhone.replace('+', '');
  
  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);
  
  // Open WhatsApp
  window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [cakes, setCakes] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingCake, setEditingCake] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [editCakeForm, setEditCakeForm] = useState({ name: "", description: "", price: "", image_url: "" });
  
  // Rental management state
  const [editingRental, setEditingRental] = useState(null);
  const [showAddRental, setShowAddRental] = useState(false);
  const [newRental, setNewRental] = useState({
    name: "",
    description: "",
    price_per_day: "",
    category: "furniture"
  });
  
  // Booking calendar state
  const [rentalBookings, setRentalBookings] = useState({});
  const [selectedBookingDate, setSelectedBookingDate] = useState(null);

  const statusOptions = [
    { value: "pending", label: "Pending", icon: Clock, color: "bg-yellow-100 text-yellow-800" },
    { value: "confirmed", label: "Confirmed", icon: CheckCircle, color: "bg-blue-100 text-blue-800" },
    { value: "preparing", label: "Preparing", icon: ChefHat, color: "bg-purple-100 text-purple-800" },
    { value: "ready", label: "Ready", icon: Package, color: "bg-green-100 text-green-800" },
    { value: "delivered", label: "Delivered", icon: Truck, color: "bg-green-200 text-green-900" },
    { value: "cancelled", label: "Cancelled", icon: XCircle, color: "bg-red-100 text-red-800" },
  ];

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchOrders();
    fetchCakes();
    fetchRentals();
    fetchRentalBookings();
  }, [navigate]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
  });

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/orders`, getAuthHeader());
      setOrders(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to fetch orders");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCakes = async () => {
    try {
      const response = await axios.get(`${API}/admin/cakes`, getAuthHeader());
      setCakes(response.data);
    } catch (error) {
      console.error("Failed to fetch cakes");
    }
  };

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/admin/rentals`, getAuthHeader());
      setRentals(response.data);
    } catch (error) {
      console.error("Failed to fetch rentals");
    }
  };

  const fetchRentalBookings = async () => {
    try {
      const response = await axios.get(`${API}/rental-bookings`);
      setRentalBookings(response.data);
    } catch (error) {
      console.error("Failed to fetch rental bookings");
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(order => 
        order.customer_name.toLowerCase().includes(term) ||
        order.phone.includes(term) ||
        order.id.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== "all") {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: newStatus }, getAuthHeader());
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updatePaymentStatus = async (orderId, paymentStatus) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/payment`, { payment_status: paymentStatus }, getAuthHeader());
      toast.success(paymentStatus === "paid" ? "Payment confirmed!" : "Payment marked as unpaid");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to update payment status");
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, getAuthHeader());
      toast.success("Order deleted");
      fetchOrders();
    } catch (error) {
      toast.error("Failed to delete order");
    }
  };

  const updateCake = async (cakeId) => {
    if (!editCakeForm.name.trim()) {
      toast.error("Please enter a cake name");
      return;
    }
    if (!editCakeForm.price || parseFloat(editCakeForm.price) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    try {
      await axios.patch(`${API}/admin/cakes/${cakeId}`, {
        name: editCakeForm.name,
        description: editCakeForm.description,
        price: parseFloat(editCakeForm.price),
        image_url: editCakeForm.image_url
      }, getAuthHeader());
      toast.success("Cake updated successfully!");
      fetchCakes();
      setEditingCake(null);
      setEditCakeForm({ name: "", description: "", price: "", image_url: "" });
    } catch (error) {
      toast.error("Failed to update cake");
    }
  };

  // Rental management functions
  const createRental = async () => {
    if (!newRental.name || !newRental.description || !newRental.price_per_day) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/rentals`, {
        ...newRental,
        price_per_day: parseFloat(newRental.price_per_day)
      }, getAuthHeader());
      toast.success("Rental item created!");
      fetchRentals();
      setShowAddRental(false);
      setNewRental({ name: "", description: "", price_per_day: "", category: "furniture" });
    } catch (error) {
      toast.error("Failed to create rental");
    }
  };

  const updateRental = async (rentalId, updates) => {
    try {
      await axios.patch(`${API}/admin/rentals/${rentalId}`, updates, getAuthHeader());
      toast.success("Rental updated!");
      fetchRentals();
      setEditingRental(null);
    } catch (error) {
      toast.error("Failed to update rental");
    }
  };

  const deleteRental = async (rentalId) => {
    if (!window.confirm("Are you sure you want to delete this rental item?")) return;
    try {
      await axios.delete(`${API}/admin/rentals/${rentalId}`, getAuthHeader());
      toast.success("Rental deleted!");
      fetchRentals();
    } catch (error) {
      toast.error("Failed to delete rental");
    }
  };

  // WhatsApp Message Templates
  const getWhatsAppMessage = (type, customerName) => {
    const name = customerName?.split(' ')[0] || 'Valued Customer';
    
    switch(type) {
      case 'thank_you':
        return `Hi ${name}! 🎂 Thank you for your order with Sis Carol Cakes! We appreciate your business. Your delicious cake is being prepared with love.`;
      case 'pickup':
        return `Hi ${name}! 🎂 Your cake order is ready for pickup at Irie Eatz Restaurant, Market Street, Ocho Rios. Please call 1876-277-2648 when you arrive. Thank you!`;
      case 'delivery':
        return `Hi ${name}! 🎂 Reminder: Your cake is ready for pickup/delivery on October 2 at Irie Eatz Restaurant, Market Street, Ocho Rios. Call 1876-277-2648 for directions.`;
      default:
        return `Hi ${name}! Thank you for ordering from Sis Carol Cakes! 🎂`;
    }
  };

  // Open WhatsApp for all customers with a specific message type
  const sendWhatsAppToAll = (messageType) => {
    const uniqueCustomers = {};
    orders.forEach(order => {
      if (order.phone && !uniqueCustomers[order.phone]) {
        uniqueCustomers[order.phone] = {
          name: order.customer_name
        };
      }
    });
    
    const customerList = Object.entries(uniqueCustomers);
    if (customerList.length === 0) {
      toast.error("No customers to notify");
      return;
    }
    
    // Open WhatsApp for first customer, show instructions for rest
    const [firstPhone, firstData] = customerList[0];
    const message = getWhatsAppMessage(messageType, firstData.name);
    openWhatsApp(firstPhone, message);
    
    if (customerList.length > 1) {
      toast.info(`Opening WhatsApp for ${customerList.length} customers. After sending, click the button again for next customer.`, { duration: 5000 });
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUsername");
    navigate("/admin");
  };

  const getStatusBadge = (status) => {
    const statusOption = statusOptions.find(s => s.value === status);
    if (!statusOption) return <Badge variant="outline">{status}</Badge>;
    return (
      <Badge className={`${statusOption.color} border-0`}>
        {statusOption.label}
      </Badge>
    );
  };

  const getStats = () => {
    return {
      total: orders.length,
      pending: orders.filter(o => o.status === "pending").length,
      confirmed: orders.filter(o => o.status === "confirmed").length,
      preparing: orders.filter(o => o.status === "preparing").length,
      ready: orders.filter(o => o.status === "ready").length,
      delivered: orders.filter(o => o.status === "delivered").length,
    };
  };

  // Get item summary - count of each cake ordered
  const getItemSummary = () => {
    const summary = {};
    orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const name = item.cake_name;
          if (!summary[name]) {
            summary[name] = { quantity: 0, revenue: 0 };
          }
          summary[name].quantity += item.quantity;
          summary[name].revenue += item.cake_price * item.quantity;
        });
      }
    });
    // Convert to array and sort by quantity
    return Object.entries(summary)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.quantity - a.quantity);
  };

  const itemSummary = getItemSummary();
  const stats = getStats();

  // Get the app URL for sharing
  const getAppUrl = () => {
    return "https://sis-carol-cakes.org/order";
  };

  const copyAppLink = () => {
    navigator.clipboard.writeText(getAppUrl());
    toast.success("Order link copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Cake className="w-10 h-10 text-primary" />
            <div>
              <span className="font-heading text-2xl font-semibold text-foreground tracking-tight">Sis Carol</span>
              <span className="text-xs text-muted-foreground block">Admin Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={copyAppLink}
              className="flex items-center gap-2 text-primary border-primary hover:bg-primary/5"
            >
              <Share2 className="w-4 h-4" /> Share Order Link
            </Button>
            <Link to="/order" target="_blank" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ExternalLink className="w-4 h-4" /> View Store
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Orders
            </TabsTrigger>
            <TabsTrigger value="summary" className="flex items-center gap-2">
              <Cake className="w-4 h-4" /> Item Summary
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="cakes" className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Manage Cakes
            </TabsTrigger>
            <TabsTrigger value="rentals" className="flex items-center gap-2">
              <PartyPopper className="w-4 h-4" /> Party Rentals
            </TabsTrigger>
            <TabsTrigger value="bookings" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Booking Calendar
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
              <Card className="p-4 bg-white border-gray-200">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total</p>
                <p className="font-heading text-2xl text-foreground" data-testid="stat-total">{stats.total}</p>
              </Card>
              <Card className="p-4 bg-yellow-50 border-yellow-200">
                <p className="text-xs text-yellow-700 uppercase tracking-wide">Pending</p>
                <p className="font-heading text-2xl text-yellow-800" data-testid="stat-pending">{stats.pending}</p>
              </Card>
              <Card className="p-4 bg-blue-50 border-blue-200">
                <p className="text-xs text-blue-700 uppercase tracking-wide">Confirmed</p>
                <p className="font-heading text-2xl text-blue-800" data-testid="stat-confirmed">{stats.confirmed}</p>
              </Card>
              <Card className="p-4 bg-purple-50 border-purple-200">
                <p className="text-xs text-purple-700 uppercase tracking-wide">Preparing</p>
                <p className="font-heading text-2xl text-purple-800">{stats.preparing}</p>
              </Card>
              <Card className="p-4 bg-green-50 border-green-200">
                <p className="text-xs text-green-700 uppercase tracking-wide">Ready</p>
                <p className="font-heading text-2xl text-green-800">{stats.ready}</p>
              </Card>
              <Card className="p-4 bg-emerald-50 border-emerald-200">
                <p className="text-xs text-emerald-700 uppercase tracking-wide">Delivered</p>
                <p className="font-heading text-2xl text-emerald-800">{stats.delivered}</p>
              </Card>
            </div>

            {/* Filters */}
            <Card className="p-4 mb-6 bg-white border-gray-200">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="admin-search-input"
                    placeholder="Search by name, phone, or order ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="admin-status-filter" className="w-full md:w-48">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {statusOptions.map(status => (
                      <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={fetchOrders} data-testid="admin-refresh-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>
            </Card>

            {/* Orders Table */}
            <Card className="bg-white border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Order ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead>Delivery</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">Loading orders...</p>
                      </TableCell>
                    </TableRow>
                  ) : filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12">
                        <Package className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">No orders found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                        <TableCell className="font-mono text-xs">{order.id.slice(0, 8)}...</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{order.customer_name}</p>
                            <p className="text-xs text-muted-foreground">{order.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{order.items?.length || 1} item(s)</p>
                            <p className="text-xs text-muted-foreground">
                              {order.items?.map(i => i.cake_name).join(", ").slice(0, 30) || order.cake_name}...
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{order.delivery_date}</p>
                            <p className="text-xs text-muted-foreground">{order.delivery_time}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-heading text-primary">J${order.total_amount.toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="text-xs w-fit">
                              {order.payment_method === "bank_transfer" ? "Bank" : "Cash"}
                            </Badge>
                            {order.payment_status === "paid" ? (
                              <Badge className="bg-green-100 text-green-800 border-0 text-xs w-fit">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-0 text-xs w-fit">
                                Unpaid
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(order.status)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {/* Payment Confirmation Button */}
                            {order.payment_status !== "paid" ? (
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={() => updatePaymentStatus(order.id, "paid")}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50 text-xs"
                                data-testid={`confirm-payment-${order.id}`}
                              >
                                <DollarSign className="w-3 h-3 mr-1" /> Confirm
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost"
                                size="sm"
                                onClick={() => updatePaymentStatus(order.id, "unpaid")}
                                className="text-gray-500 hover:text-gray-700 text-xs"
                              >
                                Undo
                              </Button>
                            )}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  onClick={() => setSelectedOrder(order)}
                                  data-testid={`view-order-${order.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle className="font-heading">Order Details</DialogTitle>
                                </DialogHeader>
                                {selectedOrder && (
                                  <div className="space-y-4 mt-4">
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="text-xs text-muted-foreground">Order ID</p>
                                        <p className="font-mono text-sm">{selectedOrder.id}</p>
                                      </div>
                                      {getStatusBadge(selectedOrder.status)}
                                    </div>
                                    
                                    <div className="border-t pt-4">
                                      <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <Cake className="w-4 h-4 text-primary" /> Items Ordered
                                      </h4>
                                      {selectedOrder.items?.map((item, idx) => (
                                        <div key={idx} className="bg-gray-50 p-2 rounded mb-2">
                                          <p className="font-medium">{item.cake_name} x {item.quantity}</p>
                                          <p className="text-sm text-muted-foreground">J${item.cake_price.toLocaleString()} each</p>
                                          {item.cake_message && <p className="text-sm">Message: "{item.cake_message}"</p>}
                                        </div>
                                      ))}
                                    </div>

                                    <div className="border-t pt-4">
                                      <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-primary" /> Customer
                                      </h4>
                                      <p>{selectedOrder.customer_name}</p>
                                      <p className="text-sm text-muted-foreground">{selectedOrder.phone}</p>
                                      {selectedOrder.email && (
                                        <p className="text-sm text-muted-foreground">{selectedOrder.email}</p>
                                      )}
                                    </div>

                                    <div className="border-t pt-4">
                                      <h4 className="font-medium mb-2 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-primary" /> Delivery
                                      </h4>
                                      <p className="text-sm">{selectedOrder.address}</p>
                                      <p className="text-sm text-muted-foreground mt-1">{selectedOrder.delivery_zone_name}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {selectedOrder.delivery_date} at {selectedOrder.delivery_time}
                                      </p>
                                    </div>

                                    <div className="border-t pt-4 flex justify-between items-center">
                                      <span className="font-medium">Total Amount</span>
                                      <span className="font-heading text-2xl text-primary">
                                        J${selectedOrder.total_amount.toLocaleString()}
                                      </span>
                                    </div>

                                    {/* Payment Confirmation */}
                                    <div className="border-t pt-4">
                                      <div className="flex items-center justify-between mb-2">
                                        <Label className="text-sm font-medium">Payment Status</Label>
                                        {selectedOrder.payment_status === "paid" ? (
                                          <Badge className="bg-green-100 text-green-800 border-0">
                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Paid
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-red-100 text-red-800 border-0">
                                            Unpaid
                                          </Badge>
                                        )}
                                      </div>
                                      {selectedOrder.payment_status !== "paid" ? (
                                        <Button 
                                          onClick={() => {
                                            updatePaymentStatus(selectedOrder.id, "paid");
                                            setSelectedOrder({...selectedOrder, payment_status: "paid"});
                                          }}
                                          className="w-full bg-green-600 hover:bg-green-700"
                                        >
                                          <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm Payment Received
                                        </Button>
                                      ) : (
                                        <Button 
                                          variant="outline"
                                          onClick={() => {
                                            updatePaymentStatus(selectedOrder.id, "unpaid");
                                            setSelectedOrder({...selectedOrder, payment_status: "unpaid"});
                                          }}
                                          className="w-full"
                                        >
                                          Mark as Unpaid
                                        </Button>
                                      )}
                                    </div>

                                    {/* Status Update */}
                                    <div className="border-t pt-4">
                                      <Label className="text-sm font-medium mb-2 block">Update Order Status</Label>
                                      <Select 
                                        value={selectedOrder.status} 
                                        onValueChange={(val) => {
                                          updateOrderStatus(selectedOrder.id, val);
                                          setSelectedOrder({...selectedOrder, status: val});
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {statusOptions.map(status => (
                                            <SelectItem key={status.value} value={status.value}>
                                              {status.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>

                            {/* Status Update Dropdown */}
                            <Select 
                              value={order.status} 
                              onValueChange={(val) => updateOrderStatus(order.id, val)}
                            >
                              <SelectTrigger className="w-28 h-8 text-xs" data-testid={`status-select-${order.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statusOptions.map(status => (
                                  <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            {/* Delete Button */}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => deleteOrder(order.id)}
                              data-testid={`delete-order-${order.id}`}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Item Summary Tab */}
          <TabsContent value="summary">
            {/* Share Link Card */}
            <Card className="p-6 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-primary" /> Share Your Store
                  </h3>
                  <p className="text-muted-foreground text-sm mt-1">Send this link to customers so they can order cakes</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-white rounded-lg px-4 py-2 border border-border flex items-center gap-2 min-w-0">
                    <span className="text-sm truncate max-w-[200px] md:max-w-[300px]">{getAppUrl()}</span>
                  </div>
                  <Button onClick={copyAppLink} className="bg-primary hover:bg-primary/90">
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                  <Link to="/order" target="_blank">
                    <Button variant="outline">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white mb-6">
              <h2 className="font-heading text-2xl mb-2">Order Summary by Item</h2>
              <p className="text-muted-foreground mb-6">Total quantities ordered for each cake type</p>
              
              {itemSummary.length === 0 ? (
                <div className="text-center py-12">
                  <Cake className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No orders yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <Card className="p-4 bg-primary/5 border-primary/20">
                      <p className="text-xs text-primary uppercase tracking-wide">Total Items Ordered</p>
                      <p className="font-heading text-3xl text-primary">
                        {itemSummary.reduce((sum, item) => sum + item.quantity, 0)}
                      </p>
                    </Card>
                    <Card className="p-4 bg-green-50 border-green-200">
                      <p className="text-xs text-green-700 uppercase tracking-wide">Total Revenue</p>
                      <p className="font-heading text-3xl text-green-800">
                        J${itemSummary.reduce((sum, item) => sum + item.revenue, 0).toLocaleString()}
                      </p>
                    </Card>
                    <Card className="p-4 bg-blue-50 border-blue-200">
                      <p className="text-xs text-blue-700 uppercase tracking-wide">Unique Items</p>
                      <p className="font-heading text-3xl text-blue-800">{itemSummary.length}</p>
                    </Card>
                    <Card className="p-4 bg-purple-50 border-purple-200">
                      <p className="text-xs text-purple-700 uppercase tracking-wide">Total Orders</p>
                      <p className="font-heading text-3xl text-purple-800">{orders.length}</p>
                    </Card>
                  </div>

                  {/* Item Breakdown Table */}
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Cake Name</TableHead>
                        <TableHead className="text-center">Quantity Ordered</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemSummary.map((item, index) => (
                        <TableRow key={item.name}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                <Cake className="w-5 h-5 text-primary" />
                              </div>
                              <span className="font-medium">{item.name}</span>
                              {index === 0 && (
                                <Badge className="bg-yellow-100 text-yellow-800 border-0 text-xs">Top Seller</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-heading text-xl">{item.quantity}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-heading text-lg text-primary">J${item.revenue.toLocaleString()}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="space-y-6">
              {/* WhatsApp Notification Header */}
              <Card className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <MessageCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-semibold text-gray-900">WhatsApp Notifications</h2>
                    <p className="text-muted-foreground mt-1">
                      Send WhatsApp messages to customers - FREE! Click a button to open WhatsApp with pre-filled message.
                    </p>
                    <p className="text-sm text-green-600 mt-2">
                      <strong>{orders.length}</strong> total orders • <strong>{new Set(orders.map(o => o.phone)).size}</strong> unique customers
                    </p>
                    <p className="text-xs text-green-700 mt-1 bg-green-100 inline-block px-2 py-1 rounded">
                      💡 Cost: FREE - Uses your own WhatsApp
                    </p>
                  </div>
                </div>
              </Card>

              {/* WhatsApp Notification Options */}
              <div className="grid md:grid-cols-3 gap-6">
                {/* Thank You Message */}
                <Card className="p-6 bg-white border-2 border-transparent hover:border-green-200 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2">Thank You Message</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Send a thank you message to customers
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-left text-sm text-muted-foreground mb-4">
                      "Hi [Name]! 🎂 Thank you for your order with Sis Carol Cakes! Your delicious cake is being prepared with love."
                    </div>
                    <Button 
                      onClick={() => sendWhatsAppToAll("thank_you")}
                      disabled={orders.length === 0}
                      className="w-full bg-green-600 hover:bg-green-700"
                      data-testid="send-thankyou-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Open WhatsApp
                    </Button>
                  </div>
                </Card>

                {/* Pickup Ready Message */}
                <Card className="p-6 bg-white border-2 border-transparent hover:border-blue-200 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2">Pickup/Delivery Ready</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Notify customers their cake is ready
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-left text-sm text-muted-foreground mb-4">
                      "Hi [Name]! 🎂 Your cake is ready at Irie Eatz Restaurant, Market Street, Ocho Rios."
                    </div>
                    <Button 
                      onClick={() => sendWhatsAppToAll("pickup")}
                      disabled={orders.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="send-pickup-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Open WhatsApp
                    </Button>
                  </div>
                </Card>

                {/* Delivery Reminder */}
                <Card className="p-6 bg-white border-2 border-transparent hover:border-purple-200 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Truck className="w-8 h-8 text-purple-600" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mb-2">March 27 Reminder</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Remind customers about March 27
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-left text-sm text-muted-foreground mb-4">
                      "Hi [Name]! 🎂 Reminder: Your cake is ready for pickup on March 27!"
                    </div>
                    <Button 
                      onClick={() => sendWhatsAppToAll("delivery")}
                      disabled={orders.length === 0}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="send-delivery-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Open WhatsApp
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Individual Customer WhatsApp */}
              <Card className="p-6 bg-white">
                <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-600" /> Message Individual Customers
                </h3>
                <p className="text-muted-foreground text-sm mb-4">Click on a customer to open WhatsApp with their number</p>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                  {orders.map((order) => (
                    <Button
                      key={order.id}
                      variant="outline"
                      className="justify-start text-left h-auto py-3 hover:bg-green-50 hover:border-green-300"
                      onClick={() => openWhatsApp(order.phone, getWhatsAppMessage('thank_you', order.customer_name))}
                    >
                      <MessageCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
                      <div className="truncate">
                        <p className="font-medium truncate">{order.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{order.phone}</p>
                      </div>
                    </Button>
                  ))}
                </div>
              </Card>

              {/* No Orders Warning */}
              {orders.length === 0 && (
                <Card className="p-8 bg-yellow-50 border-yellow-200 text-center">
                  <Package className="w-12 h-12 mx-auto text-yellow-600 mb-3" />
                  <h3 className="font-heading text-lg font-semibold text-yellow-800">No Orders Yet</h3>
                  <p className="text-yellow-700 mt-1">
                    There are no orders to send notifications to.
                  </p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Cakes Tab */}
          <TabsContent value="cakes">
            <Card className="p-6 bg-white">
              <h2 className="font-heading text-2xl mb-6">Manage Cake Images</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {cakes.map((cake) => (
                  <Card key={cake.id} className="overflow-hidden">
                    <div className="aspect-video relative">
                      <img 
                        src={cake.image_url} 
                        alt={cake.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                        }}
                      />
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            size="icon"
                            className="absolute top-2 right-2 bg-white/90 hover:bg-white text-primary"
                            onClick={() => {
                              setEditingCake(cake);
                              setEditCakeForm({
                                name: cake.name,
                                description: cake.description,
                                price: cake.price.toString(),
                                image_url: cake.image_url
                              });
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                          <DialogHeader>
                            <DialogTitle>Edit Cake - {cake.name}</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto">
                            <div>
                              <Label>Cake Name *</Label>
                              <Input
                                value={editCakeForm.name}
                                onChange={(e) => setEditCakeForm({...editCakeForm, name: e.target.value})}
                                placeholder="Enter cake name"
                                className="mt-2"
                              />
                            </div>
                            <div>
                              <Label>Description</Label>
                              <Textarea
                                value={editCakeForm.description}
                                onChange={(e) => setEditCakeForm({...editCakeForm, description: e.target.value})}
                                placeholder="Enter description"
                                className="mt-2"
                                rows={3}
                              />
                            </div>
                            <div>
                              <Label>Price (J$) *</Label>
                              <Input
                                type="number"
                                value={editCakeForm.price}
                                onChange={(e) => setEditCakeForm({...editCakeForm, price: e.target.value})}
                                placeholder="1000"
                                className="mt-2"
                              />
                            </div>
                            <div>
                              <Label>Image URL</Label>
                              <Input
                                value={editCakeForm.image_url}
                                onChange={(e) => setEditCakeForm({...editCakeForm, image_url: e.target.value})}
                                placeholder="https://example.com/image.jpg"
                                className="mt-2"
                              />
                            </div>
                            {editCakeForm.image_url && (
                              <div>
                                <Label>Image Preview</Label>
                                <img 
                                  src={editCakeForm.image_url} 
                                  alt="Preview"
                                  className="w-full h-40 object-cover rounded-lg mt-2"
                                  onError={(e) => {
                                    e.target.src = "https://via.placeholder.com/300x200?text=Invalid+URL";
                                  }}
                                />
                              </div>
                            )}
                            <div className="flex gap-2 pt-2">
                              <Button 
                                onClick={() => updateCake(cake.id)}
                                className="flex-1 bg-primary"
                              >
                                <Save className="w-4 h-4 mr-2" /> Save Changes
                              </Button>
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold">{cake.name}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{cake.description}</p>
                      <p className="font-heading text-primary mt-2">J${cake.price.toLocaleString()}</p>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Rentals Tab */}
          <TabsContent value="rentals">
            <Card className="p-6 bg-white">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-heading text-2xl">Party Rental Equipment</h2>
                  <p className="text-muted-foreground">Manage rental items and prices</p>
                </div>
                <Button onClick={() => setShowAddRental(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" /> Add New Item
                </Button>
              </div>

              {/* Add New Rental Form */}
              {showAddRental && (
                <Card className="p-4 mb-6 bg-purple-50 border-purple-200">
                  <h3 className="font-medium mb-4">Add New Rental Item</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label>Name</Label>
                      <Input 
                        value={newRental.name}
                        onChange={(e) => setNewRental({...newRental, name: e.target.value})}
                        placeholder="e.g. Large Table"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input 
                        value={newRental.description}
                        onChange={(e) => setNewRental({...newRental, description: e.target.value})}
                        placeholder="Brief description"
                      />
                    </div>
                    <div>
                      <Label>Price per Day (J$)</Label>
                      <Input 
                        type="number"
                        value={newRental.price_per_day}
                        onChange={(e) => setNewRental({...newRental, price_per_day: e.target.value})}
                        placeholder="1500"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={newRental.category} onValueChange={(v) => setNewRental({...newRental, category: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="furniture">🪑 Furniture</SelectItem>
                          <SelectItem value="shelter">⛺ Shelter</SelectItem>
                          <SelectItem value="decorations">🎈 Decorations</SelectItem>
                          <SelectItem value="audio">🔊 Audio</SelectItem>
                          <SelectItem value="serving">🍽️ Serving</SelectItem>
                          <SelectItem value="entertainment">🎪 Entertainment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button onClick={createRental} className="bg-purple-600 hover:bg-purple-700">
                      <Save className="w-4 h-4 mr-2" /> Save Item
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddRental(false)}>Cancel</Button>
                  </div>
                </Card>
              )}

              {/* Rentals Table */}
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Price/Day</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rentals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <PartyPopper className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-muted-foreground">No rental items yet</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rentals.map((rental) => (
                      <TableRow key={rental.id}>
                        <TableCell>
                          {editingRental === rental.id ? (
                            <Input 
                              defaultValue={rental.name}
                              id={`name-${rental.id}`}
                              className="w-full"
                            />
                          ) : (
                            <span className="font-medium">{rental.name}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRental === rental.id ? (
                            <Select defaultValue={rental.category} id={`cat-${rental.id}`}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="furniture">🪑 Furniture</SelectItem>
                                <SelectItem value="shelter">⛺ Shelter</SelectItem>
                                <SelectItem value="decorations">🎈 Decorations</SelectItem>
                                <SelectItem value="audio">🔊 Audio</SelectItem>
                                <SelectItem value="serving">🍽️ Serving</SelectItem>
                                <SelectItem value="entertainment">🎪 Entertainment</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="outline">
                              {rental.category === 'furniture' && '🪑'}
                              {rental.category === 'shelter' && '⛺'}
                              {rental.category === 'decorations' && '🎈'}
                              {rental.category === 'audio' && '🔊'}
                              {rental.category === 'serving' && '🍽️'}
                              {rental.category === 'entertainment' && '🎪'}
                              {' '}{rental.category}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-xs">
                          {editingRental === rental.id ? (
                            <Input 
                              defaultValue={rental.description}
                              id={`desc-${rental.id}`}
                            />
                          ) : (
                            <span className="text-sm text-muted-foreground truncate block">{rental.description}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {editingRental === rental.id ? (
                            <Input 
                              type="number"
                              defaultValue={rental.price_per_day}
                              id={`price-${rental.id}`}
                              className="w-24"
                            />
                          ) : (
                            <span className="font-heading text-primary">J${rental.price_per_day.toLocaleString()}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            className={rental.available !== false ? "bg-green-100 text-green-800 hover:bg-red-100 hover:text-red-800" : "bg-red-100 text-red-800 hover:bg-green-100 hover:text-green-800"}
                            onClick={() => updateRental(rental.id, { available: rental.available === false })}
                          >
                            {rental.available !== false ? "Available" : "Unavailable"}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {editingRental === rental.id ? (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => {
                                  const name = document.getElementById(`name-${rental.id}`)?.value;
                                  const description = document.getElementById(`desc-${rental.id}`)?.value;
                                  const price = document.getElementById(`price-${rental.id}`)?.value;
                                  updateRental(rental.id, {
                                    name,
                                    description,
                                    price_per_day: parseFloat(price)
                                  });
                                }}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingRental(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setEditingRental(rental.id)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => deleteRental(rental.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Booking Calendar Tab */}
          <TabsContent value="bookings">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-xl font-semibold">Rental Booking Calendar</h3>
                  <p className="text-muted-foreground">View which equipment is booked on each date</p>
                </div>
                <Button variant="outline" onClick={fetchRentalBookings}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>

              {/* Calendar Legend */}
              <div className="flex gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
                  <span className="text-sm">Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
                  <span className="text-sm">Has Bookings</span>
                </div>
              </div>

              {/* Bookings List */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Dates with bookings */}
                <div>
                  <h4 className="font-semibold mb-4">Upcoming Booked Dates</h4>
                  {Object.keys(rentalBookings).length === 0 ? (
                    <p className="text-muted-foreground">No rental bookings yet</p>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {Object.entries(rentalBookings)
                        .sort(([a], [b]) => new Date(a) - new Date(b))
                        .map(([date, items]) => (
                          <Card 
                            key={date} 
                            className={`p-4 cursor-pointer transition-all ${selectedBookingDate === date ? 'ring-2 ring-primary' : ''}`}
                            onClick={() => setSelectedBookingDate(date)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium">
                                  {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              </div>
                              <Badge className="bg-red-100 text-red-800">
                                {items.length} item{items.length > 1 ? 's' : ''}
                              </Badge>
                            </div>
                          </Card>
                        ))}
                    </div>
                  )}
                </div>

                {/* Selected date details */}
                <div>
                  <h4 className="font-semibold mb-4">
                    {selectedBookingDate 
                      ? `Bookings for ${new Date(selectedBookingDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`
                      : 'Select a date to view details'
                    }
                  </h4>
                  {selectedBookingDate && rentalBookings[selectedBookingDate] ? (
                    <Card className="p-4 bg-red-50 border-red-200">
                      <h5 className="font-medium text-red-800 mb-3">Equipment Booked:</h5>
                      <div className="space-y-2">
                        {rentalBookings[selectedBookingDate].map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                            <span className="font-medium">{item.rental_name}</span>
                            <Badge variant="outline">Qty: {item.quantity}</Badge>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ) : (
                    <Card className="p-8 bg-gray-50 text-center">
                      <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-muted-foreground">Click a date on the left to see booking details</p>
                    </Card>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
